<script>
/**
 * 거래 정책 상세 페이지.
 *
 * 기능:
 * - 거래 정책 상세 조회 (tradePolicyCode로 API 호출)
 * - 수정: 편집 모드 진입 후 수정 저장 (HQ 전용)
 *
 * 등록/삭제 없음. 정책은 고정 데이터로 수정만 가능.
 */
import { getTradePolicyDetail, updateTradePolicy } from '@/api/tradePolicy.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'TradePolicyDetailPage',

  data() {
    return {
      /** 현재 조회 중인 정책 데이터 */
      policy: null,

      /** 편집 모드 여부 */
      editMode: false,

      /** 편집 폼 데이터 */
      editForm: {
        policyName: '',
        upperLimitPct: '',
        lowerLimitPct: '',
        buyHqFeePct: '',
        buyUseBranchFeePct: '',
        buyRecBranchFeePct: '',
        p2pAskSellHqFeePct: '',
        p2pAskSellUseBranchFeePct: '',
        p2pAskSellRecBranchFeePct: '',
        sellHqFeePct: '',
        sellUseBranchFeePct: '',
        sellRecBranchFeePct: '',
        penaltyFeePct: '',
        penaltyFeeDays: '',
        baseFeeType: '',
        baseFeeValue: '',
        activeYn: 'Y',
        effectiveFrom: '',
        effectiveTo: '',
        memo: '',
      },

      /* ── UI 상태 ── */
      loading: false,
      saving: false,

      /** 오류 메시지 */
      errorMsg: '',
      saveErrorMsg: '',

      /** 로그인한 관리자 정보 */
      adminInfo: {},
    }
  },

  computed: {
    /** 현재 조회할 정책코드 (라우트 파라미터) */
    tradePolicyCode() {
      return this.$route.params.tradePolicyCode
    },
    /** HQ 관리자 여부: 수정 버튼 노출 제어 */
    isHq() {
      return this.adminInfo.adminType === 'HQ'
    },
    /** 활성여부 옵션 */
    activeYnOptions() {
      return [
        { title: '활성', value: 'Y' },
        { title: '비활성', value: 'N' },
      ]
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    this.fetchDetail()
  },

  methods: {
    /** 정책 상세 데이터를 서버에서 로딩한다 */
    async fetchDetail() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getTradePolicyDetail(this.tradePolicyCode)
        if (res.data?.code === 0) {
          this.policy = res.data
        } else {
          this.errorMsg = res.data?.message || '정책 정보를 불러올 수 없습니다.'
        }
      } catch (e) {
        console.error('[TradePolicyDetailPage] fetchDetail error:', e)
        this.errorMsg = '정책 정보를 불러오는 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    /** 편집 모드 진입: 현재 정책 데이터를 폼에 복사 */
    enterEditMode() {
      if (!this.policy) return
      this.editForm = {
        policyName: this.policy.policyName || '',
        upperLimitPct: this.toInputNum(this.policy.upperLimitPct),
        lowerLimitPct: this.toInputNum(this.policy.lowerLimitPct),
        buyHqFeePct: this.toInputNum(this.policy.buyHqFeePct),
        buyUseBranchFeePct: this.toInputNum(this.policy.buyUseBranchFeePct),
        buyRecBranchFeePct: this.toInputNum(this.policy.buyRecBranchFeePct),
        p2pAskSellHqFeePct: this.toInputNum(this.policy.p2pAskSellHqFeePct),
        p2pAskSellUseBranchFeePct: this.toInputNum(this.policy.p2pAskSellUseBranchFeePct),
        p2pAskSellRecBranchFeePct: this.toInputNum(this.policy.p2pAskSellRecBranchFeePct),
        sellHqFeePct: this.toInputNum(this.policy.sellHqFeePct),
        sellUseBranchFeePct: this.toInputNum(this.policy.sellUseBranchFeePct),
        sellRecBranchFeePct: this.toInputNum(this.policy.sellRecBranchFeePct),
        baseFeeType: this.policy.baseFeeType || '',
        baseFeeValue: this.toInputNum(this.policy.baseFeeValue),
        penaltyFeePct: this.toInputNum(this.policy.penaltyFeePct),
        penaltyFeeDays:
          this.policy.penaltyFeeDays != null && this.policy.penaltyFeeDays !== ''
            ? String(this.policy.penaltyFeeDays)
            : '',
        activeYn: this.policy.activeYn || 'Y',
        effectiveFrom: this.toDatetimeLocal(this.policy.effectiveFrom),
        effectiveTo: this.toDatetimeLocal(this.policy.effectiveTo),
        memo: this.policy.memo || '',
      }
      this.saveErrorMsg = ''
      this.editMode = true
    },

    /** 편집 취소: 폼 초기화 후 편집 모드 종료 */
    cancelEdit() {
      this.editMode = false
      this.saveErrorMsg = ''
    },

    /** 수정 저장 */
    async handleSave() {
      this.saveErrorMsg = ''
      const v = (val) => (val != null && val !== '' ? Number(val) : null)
      const upper = v(this.editForm.upperLimitPct)
      const lower = v(this.editForm.lowerLimitPct)
      if (upper == null || upper < 0 || upper > 100) {
        this.saveErrorMsg = '상한가 퍼센트는 0~100 사이여야 합니다.'
        return
      }
      if (lower == null || lower < 0 || lower > 100) {
        this.saveErrorMsg = '하한가 퍼센트는 0~100 사이여야 합니다.'
        return
      }
      const buyHq = v(this.editForm.buyHqFeePct)
      const buyUse = v(this.editForm.buyUseBranchFeePct)
      const buyRec = v(this.editForm.buyRecBranchFeePct)
      const p2pSellHq = v(this.editForm.p2pAskSellHqFeePct)
      const p2pSellUse = v(this.editForm.p2pAskSellUseBranchFeePct)
      const p2pSellRec = v(this.editForm.p2pAskSellRecBranchFeePct)
      const sellHq = v(this.editForm.sellHqFeePct)
      const sellUse = v(this.editForm.sellUseBranchFeePct)
      const sellRec = v(this.editForm.sellRecBranchFeePct)
      if ([buyHq, buyUse, buyRec, p2pSellHq, p2pSellUse, p2pSellRec, sellHq, sellUse, sellRec].some(n => n == null || n < 0 || n > 100)) {
        this.saveErrorMsg = '모든 수수료는 0~100 사이여야 합니다.'
        return
      }

      const penaltyPct = v(this.editForm.penaltyFeePct)
      if (penaltyPct != null && (penaltyPct < 0 || penaltyPct > 100)) {
        this.saveErrorMsg = '패널티 퍼센트는 0~100 사이여야 합니다.'
        return
      }
      let penaltyDays = null
      const rawDays = this.editForm.penaltyFeeDays
      if (rawDays != null && String(rawDays).trim() !== '') {
        const d = parseInt(String(rawDays).trim(), 10)
        if (Number.isNaN(d) || d < 0) {
          this.saveErrorMsg = '패널티 기준 영업일은 0 이상의 정수여야 합니다.'
          return
        }
        penaltyDays = d
      }

      // 1g당 기본 수수료. 방식과 값은 짝이다 - 둘 다 비면 '수수료 없음',
      // 넣을 거면 둘 다 있어야 한다. 서버와 DB 도 같은 규칙으로 막는다.
      const baseType = (this.editForm.baseFeeType || '').trim()
      const baseRaw = String(this.editForm.baseFeeValue ?? '').trim()
      let baseValue = null
      if (baseType === '' && baseRaw === '') {
        baseValue = null
      } else if (baseType === '' || baseRaw === '') {
        this.saveErrorMsg = '기본 수수료는 방식과 값을 함께 입력하거나 둘 다 비워 주세요.'
        return
      } else {
        const n = Number(baseRaw)
        if (Number.isNaN(n) || n < 0) {
          this.saveErrorMsg = '기본 수수료는 0 이상이어야 합니다.'
          return
        }
        if (baseType === 'PCT' && n > 100) {
          this.saveErrorMsg = '기본 수수료 퍼센트는 0~100 사이여야 합니다.'
          return
        }
        baseValue = n
      }

      this.saving = true
      try {
        const payload = {
          policyName: this.editForm.policyName?.trim() || this.policy.policyName,
          upperLimitPct: upper,
          lowerLimitPct: lower,
          buyHqFeePct: buyHq,
          buyUseBranchFeePct: buyUse,
          buyRecBranchFeePct: buyRec,
          p2pAskSellHqFeePct: p2pSellHq,
          p2pAskSellUseBranchFeePct: p2pSellUse,
          p2pAskSellRecBranchFeePct: p2pSellRec,
          sellHqFeePct: sellHq,
          sellUseBranchFeePct: sellUse,
          sellRecBranchFeePct: sellRec,
          penaltyFeePct: penaltyPct,
          penaltyFeeDays: penaltyDays,
          baseFeeType: baseValue === null ? null : baseType,
          baseFeeValue: baseValue,
          activeYn: this.editForm.activeYn,
          effectiveFrom: this.editForm.effectiveFrom || null,
          effectiveTo: this.editForm.effectiveTo || null,
          memo: this.editForm.memo || null,
        }
        const res = await updateTradePolicy(this.tradePolicyCode, payload)
        if (res.data?.code === 0) {
          this.policy = res.data
          this.editMode = false
        } else {
          this.saveErrorMsg = res.data?.message || '수정 중 오류가 발생했습니다.'
        }
      } catch (e) {
        console.error('[TradePolicyDetailPage] handleSave error:', e)
        this.saveErrorMsg = '수정 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    /** BigDecimal 등 → input용 문자열 (소수점 2자리) */
    toInputNum(val) {
      return val != null ? String(Number(val)) : ''
    },

    /** API datetime → datetime-local input 값 (YYYY-MM-DDTHH:mm) */
    toDatetimeLocal(dt) {
      if (!dt) return ''
      const s = String(dt)
      if (s.length >= 16) return s.substring(0, 16)
      if (s.length >= 10) return s.substring(0, 10) + 'T00:00'
      return ''
    },

    /** 날짜/시간 포맷: YYYY-MM-DD HH:mm */
    formatDateTime(dt) {
      return dt ? dt.substring(0, 16).replace('T', ' ') : '-'
    },

    /** 퍼센트 표시: 소수점 2자리 */
    baseFeeTypeLabel(type) {
      if (type === 'PCT') return '퍼센트 (%)'
      if (type === 'AMOUNT') return '금액 (원/g)'
      return '없음'
    },

    /** 방식에 따라 단위가 달라서 값만 따로 찍지 않고 같이 만든다. */
    baseFeeValueLabel(policy) {
      if (!policy || !policy.baseFeeType || policy.baseFeeValue == null) return '-'
      const n = Number(policy.baseFeeValue)
      if (Number.isNaN(n)) return '-'
      return policy.baseFeeType === 'AMOUNT'
        ? n.toLocaleString('ko-KR') + '원/g'
        : n + '%'
    },

    formatPct(val) {
      return val != null ? Number(val).toFixed(2) + '%' : '-'
    },

    /** 목록으로 돌아가기 */
    goBack() {
      this.$router.push('/trade-policy')
    },
  },
}
</script>

<template>
  <div>
    <!-- 페이지 헤더 -->
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="text-primary breadcrumb-link" @click="$router.push('/trade-policy')">정책설정</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>정책 상세</span>
        </div>
        <div class="text-h5 font-weight-bold">정책 상세</div>
        <div class="text-body-2 text-grey mt-1">거래 정책 조회 및 수정</div>
      </div>

      <!-- HQ 전용 수정 버튼 -->
      <div v-if="isHq && policy" class="d-flex ga-2">
        <template v-if="!editMode">
          <v-btn
            variant="tonal"
            color="primary"
            rounded="lg"
            prepend-icon="mdi-pencil-outline"
            @click="enterEditMode"
          >
            수정
          </v-btn>
        </template>
        <template v-else>
          <v-btn variant="text" :disabled="saving" @click="cancelEdit">취소</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            rounded="lg"
            :loading="saving"
            prepend-icon="mdi-check"
            @click="handleSave"
          >
            저장
          </v-btn>
        </template>
      </div>
    </div>

    <!-- 로딩 -->
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <!-- 오류 -->
    <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" rounded="lg" class="mb-4">
      {{ errorMsg }}
    </v-alert>

    <template v-if="policy">
      <!-- 수정 오류 -->
      <v-alert
        v-if="saveErrorMsg"
        type="error"
        variant="tonal"
        density="compact"
        rounded="lg"
        class="mb-4"
      >
        {{ saveErrorMsg }}
      </v-alert>

      <!-- 기본 정보 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-primary-soft">
            <v-icon color="primary" size="18">mdi-cog-outline</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">기본 정보</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">정책코드</div>
                <div class="dl-value text-caption text-medium-emphasis font-weight-medium">{{ policy.tradePolicyCode }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">정책명</div>
                <div class="dl-value font-weight-bold">{{ policy.policyName || '-' }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">현물구분</div>
                <div class="dl-value">
                  <v-chip size="x-small" color="primary" variant="tonal" label class="font-weight-medium">{{ policy.spotType }}</v-chip>
                </div>
              </div>
              <div class="dl-item">
                <div class="dl-label">활성여부</div>
                <div class="dl-value">
                  <v-chip size="x-small" :color="policy.activeYn === 'Y' ? 'success' : 'grey'" variant="tonal" label class="font-weight-medium">
                    {{ policy.activeYn === 'Y' ? '활성' : '비활성' }}
                  </v-chip>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">정책코드</div>
              <div class="dl-value text-caption text-medium-emphasis mb-3">{{ policy.tradePolicyCode }} (수정 불가)</div>
            </div>
            <div class="edit-field-group">
              <div class="field-label">정책명</div>
              <v-text-field
                v-model="editForm.policyName"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">활성여부</div>
              <v-select
                v-model="editForm.activeYn"
                :items="activeYnOptions"
                item-title="title"
                item-value="value"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 가격 정책 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-amber-soft">
            <v-icon color="amber-darken-2" size="18">mdi-chart-line</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">가격 정책</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">상한가 퍼센트</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.upperLimitPct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">하한가 퍼센트</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.lowerLimitPct) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">상한가 퍼센트 *</div>
              <v-text-field
                v-model="editForm.upperLimitPct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">하한가 퍼센트 *</div>
              <v-text-field
                v-model="editForm.lowerLimitPct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 구매 수수료 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-teal-soft">
            <v-icon color="teal" size="18">mdi-cash-plus</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">호가 매수 수수료 (%)</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">본사</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.buyHqFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">이용대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.buyUseBranchFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">추천대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.buyRecBranchFeePct) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">본사 호가 매수 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.buyHqFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">이용대리점 호가 매수 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.buyUseBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">추천대리점 호가 매수 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.buyRecBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 호가 매도 수수료 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-deep-purple-soft">
            <v-icon color="deep-purple" size="18">mdi-swap-horizontal</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">호가 매도 수수료 (%)</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">본사</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.p2pAskSellHqFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">이용대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.p2pAskSellUseBranchFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">추천대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.p2pAskSellRecBranchFeePct) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">본사 호가 매도 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.p2pAskSellHqFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">이용대리점 호가 매도 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.p2pAskSellUseBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">추천대리점 호가 매도 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.p2pAskSellRecBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 상품 판매 수수료 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-indigo-soft">
            <v-icon color="indigo" size="18">mdi-cash-minus</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">상품 판매 수수료 (%)</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">본사</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.sellHqFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">이용대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.sellUseBranchFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">추천대리점</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.sellRecBranchFeePct) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">본사 상품 판매 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.sellHqFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">이용대리점 상품 판매 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.sellUseBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">추천대리점 상품 판매 수수료 (%) *</div>
              <v-text-field
                v-model="editForm.sellRecBranchFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 1g당 기본 수수료 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-orange-soft">
            <v-icon color="orange-darken-2" size="18">mdi-scale-balance</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">1g당 기본 수수료</span>
          <span class="text-caption text-medium-emphasis ml-2">
            공용 서버의 기준시세에 얹어 기본가격을 만듭니다
          </span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">방식</div>
                <div class="dl-value font-weight-medium">{{ baseFeeTypeLabel(policy.baseFeeType) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">값</div>
                <div class="dl-value font-weight-medium">{{ baseFeeValueLabel(policy) }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">방식</div>
              <v-select
                v-model="editForm.baseFeeType"
                :items="[
                  { title: '없음', value: '' },
                  { title: '퍼센트 (%)', value: 'PCT' },
                  { title: '금액 (원/g)', value: 'AMOUNT' },
                ]"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                hide-details="auto"
                hint="둘 중 하나만 씁니다. '없음' 이면 기준시세가 그대로 기본가격입니다"
                persistent-hint
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">
                값 ({{ editForm.baseFeeType === 'AMOUNT' ? '원/g' : '%' }})
              </div>
              <v-text-field
                v-model="editForm.baseFeeValue"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                step="0.01"
                :disabled="!editForm.baseFeeType"
                hide-details="auto"
                hint="감정평가·호가 체결 수수료와는 다른 층입니다 — 여기는 1g당 가격에 얹는 몫입니다"
                persistent-hint
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 패널티 정책 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-orange-soft">
            <v-icon color="orange-darken-2" size="18">mdi-alert-decagram-outline</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">패널티 정책</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">패널티 퍼센트</div>
                <div class="dl-value font-weight-medium">{{ formatPct(policy.penaltyFeePct) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">패널티 기준 영업일</div>
                <div class="dl-value font-weight-medium">{{ policy.penaltyFeeDays != null ? policy.penaltyFeeDays : '-' }}</div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">패널티 퍼센트 (%)</div>
              <v-text-field
                v-model="editForm.penaltyFeePct"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                max="100"
                step="0.01"
                hide-details="auto"
                hint="비우면 기존값 유지"
                persistent-hint
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">패널티 기준 영업일</div>
              <v-text-field
                v-model="editForm.penaltyFeeDays"
                type="number"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                min="0"
                step="1"
                hide-details="auto"
                hint="비우면 기존값 유지"
                persistent-hint
              />
            </div>
          </template>
        </v-card-text>
      </v-card>

      <!-- 적용 기간 및 메모 카드 -->
      <v-card rounded="xl" elevation="0" class="detail-card mb-4">
        <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
          <div class="header-icon-wrap bg-blue-grey-soft">
            <v-icon color="blue-grey" size="18">mdi-calendar-range</v-icon>
          </div>
          <span class="text-subtitle-2 font-weight-bold">적용 기간 및 메모</span>
        </div>
        <v-divider />
        <v-card-text class="pa-5">
          <template v-if="!editMode">
            <div class="dl-grid">
              <div class="dl-item">
                <div class="dl-label">적용시작일시</div>
                <div class="dl-value text-medium-emphasis">{{ formatDateTime(policy.effectiveFrom) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">적용종료일시</div>
                <div class="dl-value text-medium-emphasis">{{ formatDateTime(policy.effectiveTo) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">등록/수정일</div>
                <div class="dl-value text-medium-emphasis">{{ formatDateTime(policy.updateDt) }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">보관료(원/g·일)</div>
                <div class="dl-value text-medium-emphasis">{{ policy.storageFeeKrwPerGDay != null ? policy.storageFeeKrwPerGDay : '-' }}</div>
              </div>
              <div class="dl-item">
                <div class="dl-label">보관 유예</div>
                <div class="dl-value text-medium-emphasis">{{ policy.storageGraceDays || '-' }}</div>
              </div>
            </div>
            <div v-if="policy.memo" class="mt-4 pt-3" style="border-top: 1px solid rgba(0,0,0,0.06);">
              <div class="dl-label mb-2">메모</div>
              <div class="dl-value" style="white-space: pre-wrap; line-height: 1.6;">{{ policy.memo }}</div>
            </div>
          </template>
          <template v-else>
            <div class="edit-field-group">
              <div class="field-label">적용시작일시</div>
              <v-text-field
                v-model="editForm.effectiveFrom"
                type="datetime-local"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">적용종료일시</div>
              <v-text-field
                v-model="editForm.effectiveTo"
                type="datetime-local"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                hide-details="auto"
              />
            </div>
            <div class="edit-field-group">
              <div class="field-label">메모</div>
              <v-textarea
                v-model="editForm.memo"
                variant="outlined"
                density="comfortable"
                rounded="lg"
                rows="3"
                auto-grow
                hide-details="auto"
              />
            </div>
          </template>
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<style scoped>
/* ── 공통 카드 ── */
.detail-card {
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.detail-card-header {
  background: rgba(0, 0, 0, 0.018);
}

/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}
.bg-primary-soft    { background: rgba(var(--v-theme-primary), 0.1); }
.bg-amber-soft     { background: rgba(255, 193, 7, 0.12); }
.bg-teal-soft      { background: rgba(0, 150, 136, 0.1); }
.bg-indigo-soft    { background: rgba(63, 81, 181, 0.1); }
.bg-deep-purple-soft { background: rgba(103, 58, 183, 0.1); }
.bg-orange-soft { background: rgba(255, 152, 0, 0.12); }
.bg-blue-grey-soft { background: rgba(96, 125, 139, 0.1); }

/* ── 브레드크럼 ── */
.breadcrumb-link {
  cursor: pointer;
}
.breadcrumb-link:hover {
  text-decoration: underline;
}

/* ── 정의 리스트 ── */
.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(0, 0, 0, 0.38);
  text-transform: uppercase;
}

.dl-value {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.82);
  line-height: 1.4;
}

/* ── 수정 폼 ── */
.edit-field-group {
  margin-bottom: 16px;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.42);
  margin-bottom: 6px;
}
</style>
